import React, { useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import ProfilePhotoDisplay from 'components/application/ProfilePhotoDisplay';
import SidePanel from 'components/application/SidePanel';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useTrainingRegisterResources } from 'components/contexts/TrainingRegisterResourceManagementContext';
import ArchiveButton from 'components/personnel/ArchiveButton';
import PersonnelForm from 'components/personnel/PersonnelForm';
import PersonnelShow from 'components/personnel/PersonnelShow';
import PersonnelSidePanelTabs from 'components/personnel/PersonnelSidePanelTabs';
import FormFooter from 'components/sidepanels/FormFooter';
import ShowFooter from 'components/sidepanels/ShowFooter';

import { focusField } from 'components/helpers/refs';
import { resourceShape } from 'components/helpers/serialisableResources';

export default function PersonnelSidePanel({
  allowShowFooterContents,
  assignableCompanies,
  assignableRoles,
  closeSidePanel,
  createPersonnel = () => undefined,
  currentPersonnel,
  currentSubcontractor,
  division,
  domainLineManager,
  domainProfilePhoto,
  domainTeams,
  isProfilePhotoLoading,
  isSubcontractor,
  lineManager,
  onArchiveButtonClick,
  onAssignableRoleAdd,
  onCancel,
  onCompanyRolesOptionChange,
  onDateFieldChange,
  onDeleteClick,
  onDivisionOptionChange,
  onEditClick,
  onFieldOptionChange,
  onNewCompanyRoleAdd,
  onPersonnelDateChange,
  onPersonnelFieldChange,
  onPersonnelInputChange,
  onPersonnelOptionChange,
  onPersonnelUpdateSubmit,
  onPhotoModalOpen,
  onRemoveRole,
  onSelectPrimary,
  onSubcontractorSelect,
  onTeamSelect,
  onTeamSelectionDelete,
  onTeamSelectionRoleChange,
  onToggle,
  personnel,
  removeErrorStyling,
  requestError,
  roles,
  setCurrentPersonnel,
  setIsSubcontractor,
  sidePanelContext,
  sidePanelIsOpen,
  subcontractor,
  submitDisabled,
  teams,
  user,
}) {
  const currentActor = useCurrentActor();

  const sidePanelBodyRef = useRef();
  const sidePanelFieldRef = useRef();

  const [scrolledAmount, setScrolledAmount] = useState(0);

  const {
    hasProfileAccessEditableAccess,
    hasRoleEditableAccess,
    hasTeamPersonnelEditableAccess,
  } = useTrainingRegisterResources();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'personnel.PersonnelSidePanel',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const formBodyContent = (
    <PersonnelForm
      assignableCompanies={assignableCompanies}
      assignableRoles={assignableRoles}
      currentPersonnel={currentPersonnel}
      currentSubcontractor={currentSubcontractor}
      domainLineManager={domainLineManager}
      domainProfilePhoto={domainProfilePhoto}
      domainTeams={domainTeams}
      hasTeamPersonnelEditableAccess={hasTeamPersonnelEditableAccess}
      isCreatableCompanyRole={hasRoleEditableAccess}
      isEditableProfileAccess={hasProfileAccessEditableAccess}
      isProfilePhotoLoading={isProfilePhotoLoading}
      isSubcontractor={isSubcontractor}
      onAssignableRoleAdd={onAssignableRoleAdd}
      onCompanyRolesOptionChange={onCompanyRolesOptionChange}
      onDateFieldChange={onDateFieldChange}
      onDivisionOptionChange={onDivisionOptionChange}
      onFieldOptionChange={onFieldOptionChange}
      onNewCompanyRoleAdd={onNewCompanyRoleAdd}
      onPersonnelDateChange={onPersonnelDateChange}
      onPersonnelFieldChange={onPersonnelFieldChange}
      onPersonnelInputChange={onPersonnelInputChange}
      onPersonnelOptionChange={onPersonnelOptionChange}
      onPhotoModalOpen={onPhotoModalOpen}
      onRemoveRole={onRemoveRole}
      onSelectPrimary={onSelectPrimary}
      onSubcontractorSelect={onSubcontractorSelect}
      onTeamSelect={onTeamSelect}
      onTeamSelectionDelete={onTeamSelectionDelete}
      onTeamSelectionRoleChange={onTeamSelectionRoleChange}
      onToggle={onToggle}
      personnel={personnel}
      removeErrorStyling={removeErrorStyling}
      requestError={requestError}
      setCurrentPersonnel={setCurrentPersonnel}
      setIsSubcontractor={setIsSubcontractor}
      sidePanelBodyRef={sidePanelBodyRef}
      sidePanelContext={sidePanelContext}
      sidePanelFieldRef={sidePanelFieldRef}
      sidePanelIsOpen={sidePanelIsOpen}
      submitDisabled={submitDisabled}
      user={user}
    />
  );

  const showBodyContent = (
    <>
      <PersonnelShow
        assignableCompanies={assignableCompanies}
        currentPersonnel={currentPersonnel}
        division={division}
        personnel={personnel}
        roles={roles}
        subcontractor={subcontractor}
      />
      <hr className='tw-m-0 tw-h-px tw-border-0 tw-bg-grey-100' />
      <PersonnelSidePanelTabs
        currentPersonnel={currentPersonnel}
        defaultDetails={[
          {
            label: i18nTCommon('personnel.personnelId'),
            text: currentPersonnel.externalId,
          },
          { label: i18nTCommon('shared.email'), text: currentPersonnel.email },
        ]}
        isCompact={false}
        key={`currentPersonnelId: ${currentPersonnel.id}`}
        lineManager={lineManager}
        sidePanelIsOpen={sidePanelIsOpen}
        teams={teams}
      />
    </>
  );

  const footerContent = () => {
    switch (sidePanelContext) {
      case 'new':
        return (
          <FormFooter
            onCancel={onCancel || closeSidePanel}
            onSubmit={createPersonnel}
            submitButtonDisabled={submitDisabled || isProfilePhotoLoading}
            text={i18nTCommon('personnel.addPersonnel')}
          />
        );
      case 'show':
        return (
          allowShowFooterContents && (
            <ShowFooter
              isCompact={true}
              isEditButtonHidden={currentPersonnel.isArchived}
              onDeleteClick={onDeleteClick}
              onEditClick={onEditClick}
              requestError={requestError}
            >
              <ArchiveButton
                disabled={
                  !currentPersonnel.isArchived &&
                  currentPersonnel.isParticipatingInOngoingProjects
                }
                disabledTooltip={i18nT('show.footer.archive.disabledTooltip', {
                  interpolation: { escapeValue: false },
                  pluralRAMSName:
                    currentActor.division.attributes.pluralRAMSName,
                })}
                isArchived={currentPersonnel.isArchived}
                isCompact={true}
                onClick={onArchiveButtonClick}
              />
            </ShowFooter>
          )
        );
      case 'edit':
        return (
          <FormFooter
            onCancel={onCancel || closeSidePanel}
            onSubmit={onPersonnelUpdateSubmit}
            submitButtonDisabled={submitDisabled || isProfilePhotoLoading}
            text={i18nTCommon('shared.saveChanges')}
          />
        );
    }
  };

  const onBackdropClick = () => {
    const panelLocked = ['new', 'edit'].includes(sidePanelContext);
    if (!panelLocked) closeSidePanel();
  };

  return (
    <SidePanel
      bodyContent={
        sidePanelContext === 'show' ? showBodyContent : formBodyContent
      }
      bodyRef={sidePanelBodyRef}
      closeCallback={closeSidePanel}
      color={sidePanelContext === 'show' ? 'secondary' : 'tertiary'}
      contentContext={sidePanelContext}
      displayClose={sidePanelContext === 'show'}
      footerContent={footerContent()}
      headerContent={
        sidePanelContext === 'show' ?
          <div className='tw-border-4 tw-absolute tw-left-1/2 tw-top-full tw--translate-x-1/2 tw--translate-y-1/2 tw-transform tw-rounded-full tw-border-solid tw-border-white'>
            <ProfilePhotoDisplay
              componentSize='lg'
              photoUrl={domainProfilePhoto?.links?.croppedUrl}
              profileInitials={`${currentPersonnel.firstName[0]}${currentPersonnel.lastName[0]}`}
            />
          </div>
        : <div className='popup__title popup__title--tertiary'>
            <h1 className='tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
              {sidePanelContext === 'edit' ?
                i18nTCommon('personnel.editPersonnel')
              : i18nTCommon('personnel.addNewPersonnel')}
            </h1>
          </div>
      }
      isOpen={sidePanelIsOpen}
      onBackdropClick={onBackdropClick}
      onOpen={() => sidePanelContext === 'new' && focusField(sidePanelFieldRef)}
      scrollHeader={sidePanelContext === 'show'}
      scrolledAmount={scrolledAmount}
      setScrolledAmount={setScrolledAmount}
      submitDisabled={submitDisabled}
    />
  );
}
PersonnelSidePanel.propTypes = {
  allowShowFooterContents: PropTypes.bool,
  assignableCompanies: PropTypes.arrayOf(resourceShape('assignableCompany')),
  assignableRoles: PropTypes.arrayOf(resourceShape('companyRole')),
  closeSidePanel: PropTypes.func,
  createPersonnel: PropTypes.func,
  currentPersonnel: PropTypes.object,
  domainTeams: PropTypes.objectOf(resourceShape('team')),
  user: resourceShape('user'),
  currentSubcontractor: PropTypes.object,
  division: resourceShape(['company', 'assignableCompany']),
  domainProfilePhoto: PropTypes.object,
  isSubcontractor: PropTypes.bool,
  lineManager: resourceShape('lineManager', 'personnel'),
  domainLineManager: resourceShape('lineManager'),
  onArchiveButtonClick: PropTypes.func,
  onCancel: PropTypes.func,
  onCompanyRolesOptionChange: PropTypes.func,
  onDateFieldChange: PropTypes.func,
  onDivisionOptionChange: PropTypes.func,
  onTeamSelectionDelete: PropTypes.func,
  onTeamSelectionRoleChange: PropTypes.func,
  onTeamSelect: PropTypes.func,
  onToggle: PropTypes.func,
  onDeleteClick: PropTypes.func,
  onFieldOptionChange: PropTypes.func,
  onAssignableRoleAdd: PropTypes.func,
  onNewCompanyRoleAdd: PropTypes.func,
  onPersonnelDateChange: PropTypes.func,
  onPersonnelFieldChange: PropTypes.func,
  onPersonnelInputChange: PropTypes.func,
  onPersonnelOptionChange: PropTypes.func,
  onRemoveRole: PropTypes.func,
  onSelectPrimary: PropTypes.func,
  personnel: resourceShape('personnel'),
  removeErrorStyling: PropTypes.func,
  requestError: PropTypes.object,
  roles: PropTypes.arrayOf(resourceShape('companyRole')),
  onPersonnelUpdateSubmit: PropTypes.func,
  setCurrentPersonnel: PropTypes.func,
  setIsSubcontractor: PropTypes.func,
  onSubcontractorSelect: PropTypes.func,
  sidePanelContext: PropTypes.oneOf(['new', 'show', 'edit']),
  sidePanelIsOpen: PropTypes.bool,
  subcontractor: resourceShape(['subcontractor', 'assignableCompany']),
  submitDisabled: PropTypes.bool,
  teams: PropTypes.arrayOf(resourceShape('team')),
  onPhotoModalOpen: PropTypes.func,
  isProfilePhotoLoading: PropTypes.bool,
};
